import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { emailChannelsAPI } from '../services/api';

interface TestEmailDialogProps {
  open: boolean;
  onClose: () => void;
  channelId?: number;
}

const TestEmailDialog: React.FC<TestEmailDialogProps> = ({
  open,
  onClose,
  channelId
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  const handleSendTest = async () => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!channelId) {
      setError('Please create the channel first before sending test emails');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await emailChannelsAPI.sendTestEmail(channelId, { to: email });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading) {
      handleSendTest();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Test Email</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Send a test email to verify your template configuration is working correctly.
          </Typography>

          <TextField
            label="Test Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            fullWidth
            type="email"
            autoFocus
            helperText="Enter an email address to receive a test email"
            disabled={loading}
          />

          {!channelId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please complete channel creation before sending test emails.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Test email sent successfully! Check your inbox.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSendTest}
          variant="contained"
          disabled={loading || !email || !channelId}
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        >
          {loading ? 'Sending...' : 'Send Test Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestEmailDialog;
